/**
 * Admin create form.
 * Role-driven fields (manager / coordinator) then POSTs contact + scope in one submit.
 */
import * as React from 'react';
import {
  NotepadText,
  User,
  Building2,
  UserCog,
  Mail,
  Target,
  MapPin,
  BookOpen,
} from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Input } from '@/components/forms/Input';
import { PhoneInput } from '@/components/forms/PhoneInput';
import { SmartContactInput } from '@/components/forms/SmartContactInput';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { Autocomplete } from '@/components/forms/Autocomplete';
import { useAdminAutocomplete } from '@/hooks/useAdminAutocomplete';
import { useFormValidation } from '@/hooks/useFormValidation';
import { resolveContactCheckType, useContactExistsCheck } from '@/hooks/useContactExistsCheck';
import { adminCreateValidationSchema } from '@/utils/validation-schemas-utils';
import { createAdmin, getAdmin } from '@/services/admins.service';
import { fetchRoles } from '@/services/roles.service';
import { getEstablishments } from '@/services/establishments.service';
import { useEstablishmentsMultiselect } from '@/hooks/useEstablishmentsMultiselect';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { useAuthStore } from '@/stores/authStore';
import { buildCreatableRoleOptions, getCreatableRoles } from '@/utils/interface-hierarchy-utils';
import { findRoleByCode, roleKind, roleMatches, roleMode } from '@/utils/role-label-utils';
import type { CreateAdminDto, CreateAdminFormState } from '@/types/admin.types';
import { ESTABLISHMENT_SEARCH_HINT } from '@/utils/format-establishment-select-label-utils';
import { ADMIN_FORM_QUERY_KEYS } from './admin-configs';
import {
  applyAdminFormField,
  buildCreateAdminDto,
  deriveAdminFormFlags,
  emptyCreateAdminForm,
  getCreateRolesHelperText,
  getResidenceDepartmentCodes,
  getSelectedRoleCodes,
  getSelectedRolesData,
  mapDepartmentCodeOptions,
  mapDepartmentIdOptions,
  mapProgramSelectOptions,
  mapScopedEstablishmentOptions,
  createAdminFieldValidationPayload,
  createAdminManagerHelperText,
  createAdminRemoteManagerHelperText,
  createAdminRemoteManagerPlaceholder,
  nextContactState,
  nextEstablishmentIdsAfterDeptChange,
  resolveCreateEstablishmentSourceUserId,
} from './admin-form-utils';
import {
  areContactsIdentical,
  contactExistsErrorMessage,
  isPrimaryContactFormValid,
  oppositeContactType,
  useDefaultPhoneCountry,
} from '@/utils/user-contact-utils';
import { SlidePanelForm } from '@/components/common/SlidePanelForm';
import { renderCrossModeAfterSaveModal } from '@/components/common/modals/CrossModeAfterSaveModal';
import { useCrossModeAfterSave } from '@/hooks/useCrossModeAfterSave';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';
import { useDepartments } from '@/hooks/useDepartments';
import { useProgramsForMode } from '@/hooks/useProgramsForMode';

/** Props for {@link CreateAdminView}. */
interface CreateAdminViewProps {
  onClose: () => void;
  onSuccess?: () => void;
  initialRole?: string;
  /** When true, the role select is always disabled (role is locked to initialRole) */
  lockInitialRole?: boolean;
}

/** Admin create form. */
export const CreateAdminView: React.FC<CreateAdminViewProps> = ({
  onClose,
  onSuccess,
  initialRole,
  lockInitialRole = false,
}) => {
  const validation = useFormValidation(adminCreateValidationSchema);

  const { elements: adminElements } = useInterfaceElements('admins', { bothModes: true });
  const defaultCountry = useDefaultPhoneCountry();
  const availableRoles = React.useMemo(() => {
    return getCreatableRoles(adminElements);
  }, [adminElements]);

  const isSingleRoleMode = availableRoles.length <= 1 || lockInitialRole;

  const { data: roles = [] } = useQuery({
    queryKey: ADMIN_FORM_QUERY_KEYS.roles,
    queryFn: fetchRoles,
  });
  const { targetMode, finishSave, handleStay, handleSwitch } = useCrossModeAfterSave({
    roles,
    listPath: '/admins',
    onSuccess,
    onClose,
  });

  const [adminData, setAdminData] = React.useState(() => emptyCreateAdminForm(defaultCountry));

  const { exists: contactExists, isChecking: isPrimaryChecking } = useContactExistsCheck({
    value: adminData.contact,
    type: resolveContactCheckType(adminData.contactType),
    enabled: adminData.contactType !== 'unknown' && adminData.contact.trim().length > 0,
  });

  const contactExistsError = contactExistsErrorMessage(contactExists, adminData.contactType);

  const secondaryContactType = oppositeContactType(adminData.contactType);
  const { exists: secondaryContactExists, isChecking: isSecondaryChecking } = useContactExistsCheck(
    {
      value: adminData.secondaryContact,
      type: secondaryContactType,
      enabled: adminData.contactType !== 'unknown' && adminData.secondaryContact.trim().length > 0,
    },
  );

  const contactsAreIdentical = areContactsIdentical(adminData.contact, adminData.secondaryContact);
  const secondaryContactExistsError = contactExistsErrorMessage(
    secondaryContactExists,
    secondaryContactType,
  );

  /** Prefill role ids from `initialRole` or the single creatable role. */
  React.useEffect(() => {
    if (initialRole && roles.length > 0) {
      const matchingRole = findRoleByCode(roles, initialRole);
      if (matchingRole) {
        const newRoleIds = [matchingRole.id];
        setAdminData((prev) => ({ ...prev, roleIds: newRoleIds }));
        validation.validateField('roleIds', newRoleIds, { ...adminData, roleIds: newRoleIds });
      }
    }

    // Single-role mode: prefill when nothing is selected.
    if (isSingleRoleMode && availableRoles.length === 1 && adminData.roleIds.length === 0) {
      const roleCode = availableRoles[0].value;
      const matchingRole = findRoleByCode(roles, roleCode);
      if (matchingRole) {
        const newRoleIds = [matchingRole.id];
        setAdminData((prev) => ({ ...prev, roleIds: newRoleIds }));
        validation.validateField('roleIds', newRoleIds, { ...adminData, roleIds: newRoleIds });
      }
    }
  }, [initialRole, roles, isSingleRoleMode, availableRoles, adminData.roleIds.length]);

  const selectedRoleCodes = React.useMemo(
    () => getSelectedRoleCodes(adminData.roleIds, roles),
    [adminData.roleIds, roles],
  );
  const selectedRolesData = React.useMemo(
    () => getSelectedRolesData(adminData.roleIds, roles),
    [roles, adminData.roleIds],
  );
  const {
    hasInPersonMode,
    isManagerCategory,
    isCoordinatorCategory,
    isCoordinatorRole,
    isCoordinatorRemoteRole,
    requiresResidenceDepartments,
    showDepartmentField,
    showProgramField,
  } = deriveAdminFormFlags({
    selectedRoleCodes,
    selectedRolesData,
  });

  // Remote mode: departments and programs
  const { data: departments = [], error: departmentsError } = useDepartments('FR');

  const departmentOptions = React.useMemo(() => mapDepartmentIdOptions(departments), [departments]);

  const { user: currentUser, roles: currentUserRoles } = useAuthStore();
  const currentUserRoleCode = currentUserRoles?.[0]?.code;
  const currentUserId = currentUser?.id;

  // Autocomplete managers when an in-person coordinator is selected.
  const {
    options: managersOptions,
    isLoading: managersLoading,
    onSearch: onManagersSearch,
  } = useAdminAutocomplete({
    roleCodes: ['manager'],
    enabled: isCoordinatorRole,
  });

  // Autocomplete remote managers when coordinator_remote is selected.
  const {
    options: managersRemoteOptions,
    isLoading: managersRemoteLoading,
    onSearch: onManagersRemoteSearch,
  } = useAdminAutocomplete({
    roleCodes: ['manager_remote'],
    enabled: isCoordinatorRemoteRole,
  });

  // Establishment source user: manager create only (in-person coordinator no longer depends on manager).
  const establishmentSourceUserId = React.useMemo(
    () =>
      resolveCreateEstablishmentSourceUserId({
        isCoordinatorRole,
        currentUserRoleCode,
        currentUserId,
      }),
    [currentUserRoleCode, currentUserId, isCoordinatorRole],
  );

  // Load scoped establishments (manager create).
  const { data: scopedEstablishmentsResponse } = useQuery({
    queryKey: ['users', establishmentSourceUserId, 'establishments'],
    queryFn: async () => {
      if (!establishmentSourceUserId) {
        return { data: [] };
      }
      const user = await getAdmin(establishmentSourceUserId);
      return {
        data: user.userEstablishments?.map((ue) => ue.establishment) || [],
      };
    },
    enabled: !!establishmentSourceUserId,
  });
  const scopedEstablishments = scopedEstablishmentsResponse?.data || [];

  const selectedResidenceDepartmentCodes = React.useMemo(
    () => getResidenceDepartmentCodes(adminData.residenceDepartmentIds, departments),
    [adminData.residenceDepartmentIds, departments],
  );

  const { data: coordinatorEstablishmentsResponse, isLoading: coordinatorEstabsLoading } = useQuery(
    {
      queryKey: ['establishments', 'coordinator-assignable', selectedResidenceDepartmentCodes],
      queryFn: () =>
        getEstablishments({
          page: 1,
          pageSize: 100,
          departments: selectedResidenceDepartmentCodes,
          prospectionStatuses: ['valide', 'en_negociation'],
        }),
      enabled: isCoordinatorRole && selectedResidenceDepartmentCodes.length > 0,
    },
  );
  const coordinatorAssignableEstablishments = coordinatorEstablishmentsResponse?.data || [];

  const {
    isLoading: estabSearchLoading,
    onSearch: onEstabSearch,
    onLoadMore: onEstabLoadMore,
    hasMore: estabHasMore,
    getOptionsForValues: getEstabOptions,
  } = useEstablishmentsMultiselect({
    operationalOnly: true,
    restrictToEstablishments: isCoordinatorRole ? coordinatorAssignableEstablishments : undefined,
  });

  // Reset establishments when departments change (in-person coordinator).
  React.useEffect(() => {
    if (!isCoordinatorRole) return;
    setAdminData((prev) => {
      const nextIds = nextEstablishmentIdsAfterDeptChange(
        prev.establishmentIds,
        selectedResidenceDepartmentCodes,
        coordinatorAssignableEstablishments,
      );
      if (nextIds === null) return prev;
      return { ...prev, establishmentIds: nextIds };
    });
  }, [isCoordinatorRole, selectedResidenceDepartmentCodes, coordinatorAssignableEstablishments]);

  const { data: programsResponse } = useProgramsForMode({
    mode: 'remote',
    pageSize: 100,
    enabled: showProgramField,
  });
  const programs = programsResponse?.data || [];

  const createMutation = useMutation({
    mutationFn: (dto: CreateAdminDto) => createAdmin(dto),
    onSuccess: (_data, dto) => {
      toast.success('Collaborateur créé avec succès !');
      finishSave(dto.roleIds);
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  /** Set one field and re-validate (contact / residence-department context). */
  const handleFieldChange = (field: keyof typeof adminData, value: any) => {
    const updatedData = applyAdminFormField(adminData, field, value);
    setAdminData(updatedData);

    validation.validateField(
      field,
      value,
      createAdminFieldValidationPayload(
        field,
        updatedData,
        adminData.contactType,
        adminData.contactCountryCode,
        roles,
      ),
    );

    validation.markAsTouched(field);
    validation.markAsDirty(field);
  };

  /** Changing contact type drops the secondary contact so we never keep an outdated email/phone. */
  const handleContactChange = (
    value: string,
    type: 'email' | 'phone' | 'unknown',
    countryCode?: string,
  ) => {
    setAdminData((prev) => nextContactState(prev, value, type, countryCode, defaultCountry));

    validation.validateField('contact', value, {
      ...adminData,
      contact: value,
      _contactType: type,
      _phoneCountryCode: countryCode || adminData.contactCountryCode,
    });

    validation.markAsTouched('contact');
    validation.markAsDirty('contact');
  };

  /** Persist secondary contact and re-validate it. */
  const handleSecondaryContactChange = (value: string) => {
    setAdminData((prev) => ({ ...prev, secondaryContact: value }));

    validation.validateField('secondaryContact', value, {
      ...adminData,
      secondaryContact: value,
      _contactType: adminData.contactType,
    });

    validation.markAsTouched('secondaryContact');
    validation.markAsDirty('secondaryContact');
  };

  /** Forward the secondary input value to `handleSecondaryContactChange`. */
  const handleSecondaryInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleSecondaryContactChange(event.target.value);
  };

  /** Mark secondary contact touched. */
  const handleSecondaryBlur = () => {
    validation.markAsTouched('secondaryContact');
  };

  /** Persist the secondary-phone country ISO. */
  const handleSecondaryCountryChange = (country: { code: string }) => {
    setAdminData((prev) => ({ ...prev, secondaryContactCountryCode: country.code }));
  };

  /** Mark a field touched so its error can show. */
  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  /** Validate then POST; DTO fields depend on coordinator vs manager vs remote. */
  const handleSubmit = () => {
    const isValid = validation.validateForm({
      ...adminData,
      _contactType: adminData.contactType,
      _phoneCountryCode: adminData.contactCountryCode,
      _contactExists: contactExists,
      _requiresResidenceDepartments: requiresResidenceDepartments,
    });

    if (!isValid) {
      toast.error(getErrorMessage('requiredFields'));
      return;
    }

    createMutation.mutate(
      buildCreateAdminDto({
        form: adminData,
        requiresResidenceDepartments,
        isCoordinatorRole,
        currentUserRoleCode,
      }),
    );
  };

  const adminRoleOptions = React.useMemo(
    () => buildCreatableRoleOptions(availableRoles, roles),
    [availableRoles, roles],
  );

  // Auto-assign managedById when an in-person coordinator is created by a manager.
  React.useEffect(() => {
    if (
      isCoordinatorRole &&
      roleKind(currentUserRoleCode) === 'manager' &&
      roleMode(currentUserRoleCode) !== 'remote' &&
      currentUserId
    ) {
      setAdminData((prev) => ({ ...prev, managedById: currentUserId }));
      validation.validateField('managedById', currentUserId, {
        ...adminData,
        managedById: currentUserId,
      });
    }
  }, [isCoordinatorRole, currentUserRoleCode, currentUserId]);

  // Auto-assign managedById when a remote coordinator is created by a remote manager.
  React.useEffect(() => {
    if (
      isCoordinatorRemoteRole &&
      roleMatches(currentUserRoleCode, 'manager', 'remote') &&
      currentUserId
    ) {
      setAdminData((prev) => ({ ...prev, managedById: currentUserId }));
      validation.validateField('managedById', currentUserId, {
        ...adminData,
        managedById: currentUserId,
      });
    }
  }, [isCoordinatorRemoteRole, currentUserRoleCode, currentUserId]);

  const isSubmitting = createMutation.isPending;

  return (
    <>
      <SlidePanelForm
        formType="create"
        title="Créer un collaborateur"
        subtitle="Renseignez les informations du collaborateur"
        onClose={onClose}
        onSubmit={handleSubmit}
        submitLabel="Créer"
        isSubmitting={isSubmitting}
        isValid={isPrimaryContactFormValid({
          primaryExists: contactExists,
          secondaryExists: secondaryContactExists,
          isPrimaryChecking,
          isSecondaryChecking,
          identical: contactsAreIdentical,
          formValid: validation.isFormValid(),
        })}
      >
        <div className="form-section">
          <h4 className="form-section-title">
            <NotepadText className="w-4 h-4" />
            Rôle(s)
          </h4>
          <MultiSelect
            label="Rôles"
            placeholder="Sélectionner les rôles"
            options={adminRoleOptions}
            value={adminData.roleIds}
            onChange={(values) => handleFieldChange('roleIds', values)}
            onBlur={() => handleBlur('roleIds')}
            disabled={isSingleRoleMode}
            helperText={getCreateRolesHelperText(isSingleRoleMode, initialRole)}
            required
            state={validation.getFieldState('roleIds')}
            error={validation.getFieldError('roleIds')}
          />
        </div>

        <div className="form-section">
          <h4 className="form-section-title">
            <User className="w-4 h-4" />
            Informations personnelles
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Prénom"
              value={adminData.firstName}
              onChange={(e) => handleFieldChange('firstName', e.target.value)}
              onBlur={() => handleBlur('firstName')}
              placeholder="Ex: Marie"
              maxLength={100}
              state={validation.getFieldState('firstName')}
              error={validation.getFieldError('firstName')}
            />
            <Input
              label="Nom"
              value={adminData.lastName}
              onChange={(e) => handleFieldChange('lastName', e.target.value)}
              onBlur={() => handleBlur('lastName')}
              placeholder="Ex: Martin"
              maxLength={100}
              state={validation.getFieldState('lastName')}
              error={validation.getFieldError('lastName')}
            />
          </div>
          <div className="grid grid-cols-1 gap-4 mt-4">
            <SmartContactInput
              label="Contact"
              value={adminData.contact}
              onContactChange={handleContactChange}
              onBlur={() => handleBlur('contact')}
              required
              state={contactExistsError ? 'error' : validation.getFieldState('contact')}
              error={contactExistsError ?? validation.getFieldError('contact')}
              helperText="Saisissez l'email ou le numéro de téléphone"
            />
            {renderCreateAdminSecondaryContact({
              contactType: adminData.contactType,
              secondaryContact: adminData.secondaryContact,
              secondaryContactCountryCode: adminData.secondaryContactCountryCode,
              existsError: secondaryContactExistsError,
              validation,
              onSecondaryInputChange: handleSecondaryInputChange,
              onSecondaryBlur: handleSecondaryBlur,
              onSecondaryCountryChange: handleSecondaryCountryChange,
            })}
          </div>
        </div>

        {renderCreateAdminScopeSections({
          adminData,
          validation,
          currentUserRoleCode,
          isCoordinatorRole,
          isCoordinatorRemoteRole,
          isCoordinatorCategory,
          isManagerCategory,
          hasInPersonMode,
          requiresResidenceDepartments,
          showDepartmentField,
          showProgramField,
          managersOptions,
          managersRemoteOptions,
          managersLoading,
          managersRemoteLoading,
          departmentOptions,
          departments,
          departmentsError,
          programs,
          selectedResidenceDepartmentCodes,
          establishmentSourceUserId,
          scopedEstablishments,
          getEstabOptions,
          estabSearchLoading,
          coordinatorEstabsLoading,
          onManagersSearch,
          onManagersRemoteSearch,
          onEstabSearch,
          onEstabLoadMore,
          estabHasMore,
          onFieldChange: handleFieldChange,
          onFieldBlur: handleBlur,
        })}
      </SlidePanelForm>
      {renderCrossModeAfterSaveModal({
        targetMode,
        listLabel: 'Administration',
        onStay: handleStay,
        onSwitch: handleSwitch,
      })}
    </>
  );
};

/** Field-state helpers used by the create-admin form. */
type AdminFormValidation = {
  getFieldState: (field: string) => 'default' | 'error' | 'success' | undefined;
  getFieldError: (field: string) => string | undefined;
};

/** Optional secondary email or phone field. */
function renderCreateAdminSecondaryContact(params: {
  contactType: string;
  secondaryContact: string;
  secondaryContactCountryCode: string;
  existsError: string | undefined;
  validation: AdminFormValidation;
  onSecondaryInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onSecondaryBlur: () => void;
  onSecondaryCountryChange: (country: { code: string }) => void;
}): React.ReactNode {
  if (params.contactType === 'unknown') return null;
  const state = params.existsError ? 'error' : params.validation.getFieldState('secondaryContact');
  const error = params.existsError ?? params.validation.getFieldError('secondaryContact');
  if (params.contactType === 'email') {
    return (
      <div className="transition-all duration-300 animate-in fade-in slide-in-from-top-2">
        <PhoneInput
          label="Téléphone (contact secondaire, optionnel)"
          value={params.secondaryContact}
          onChange={params.onSecondaryInputChange}
          onBlur={params.onSecondaryBlur}
          countryCode={params.secondaryContactCountryCode}
          onCountryChange={params.onSecondaryCountryChange}
          state={state}
          error={error}
          helperText="Numéro secondaire pour joindre cet administrateur"
        />
      </div>
    );
  }
  return (
    <div className="transition-all duration-300 animate-in fade-in slide-in-from-top-2">
      <Input
        type="text"
        label="Email (contact secondaire, optionnel)"
        value={params.secondaryContact}
        onChange={params.onSecondaryInputChange}
        onBlur={params.onSecondaryBlur}
        icon={Mail}
        placeholder="Ex: contact@example.com"
        state={state}
        error={error}
        helperText="Email secondaire pour joindre cet administrateur"
      />
    </div>
  );
}

/** Shared props for create-admin scope sections. */
type CreateAdminScopeParams = {
  adminData: CreateAdminFormState;
  validation: AdminFormValidation;
  currentUserRoleCode: string | undefined;
  isCoordinatorRole: boolean;
  isCoordinatorRemoteRole: boolean;
  isCoordinatorCategory: boolean;
  isManagerCategory: boolean;
  hasInPersonMode: boolean;
  requiresResidenceDepartments: boolean;
  showDepartmentField: boolean;
  showProgramField: boolean;
  managersOptions: Array<{ value: string; label: string }>;
  managersRemoteOptions: Array<{ value: string; label: string }>;
  managersLoading: boolean;
  managersRemoteLoading: boolean;
  departmentOptions: Array<{ value: string; label: string }>;
  departments: Array<{ id: string; code: string; name: string }>;
  departmentsError: unknown;
  programs: Array<{ id: string; name?: string }>;
  selectedResidenceDepartmentCodes: string[];
  establishmentSourceUserId: string | undefined;
  scopedEstablishments: unknown[];
  getEstabOptions: (ids: string[]) => Array<{ value: string; label: string }>;
  estabSearchLoading: boolean;
  onEstabLoadMore: () => void;
  estabHasMore: boolean;
  coordinatorEstabsLoading: boolean;
  onManagersSearch: (query: string) => void;
  onManagersRemoteSearch: (query: string) => void;
  onEstabSearch: (query: string) => void;
  onFieldChange: (field: keyof CreateAdminFormState, value: unknown) => void;
  onFieldBlur: (field: string) => void;
};

/** Supervisor manager picker for coordinators. */
function renderCreateAdminManagerSection(params: CreateAdminScopeParams): React.ReactNode {
  if (!params.isCoordinatorRole) return null;
  function handleManagedByChange(value: string): void {
    params.onFieldChange('managedById', value);
  }
  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <UserCog className="w-4 h-4" />
        Manager superviseur
      </h4>
      <Autocomplete
        label="Manager"
        placeholder="Sélectionner un manager (optionnel)"
        options={params.managersOptions}
        value={params.adminData.managedById}
        onChange={handleManagedByChange}
        onSearch={params.onManagersSearch}
        isLoading={params.managersLoading}
        minSearchLength={2}
        disabled={
          roleKind(params.currentUserRoleCode) === 'manager' &&
          roleMode(params.currentUserRoleCode) !== 'remote'
        }
        helperText={createAdminManagerHelperText(params.currentUserRoleCode)}
      />
    </div>
  );
}

/** Residence-department picker when required. */
function renderCreateAdminResidenceSection(params: CreateAdminScopeParams): React.ReactNode {
  if (!params.requiresResidenceDepartments) return null;
  function handleChange(values: string[]): void {
    params.onFieldChange('residenceDepartmentIds', values);
  }
  function handleBlur(): void {
    params.onFieldBlur('residenceDepartmentIds');
  }
  const error = params.departmentsError
    ? getErrorMessage(params.departmentsError)
    : params.validation.getFieldError('residenceDepartmentIds');
  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <MapPin className="w-4 h-4" />
        Département
      </h4>
      <MultiSelect
        label="Département(s)"
        placeholder="Sélectionner un ou plusieurs départements"
        options={params.departmentOptions}
        value={params.adminData.residenceDepartmentIds}
        onChange={handleChange}
        onBlur={handleBlur}
        maxVisibleTags={3}
        required
        state={params.validation.getFieldState('residenceDepartmentIds')}
        error={error}
      />
    </div>
  );
}

/** Remote-manager picker for remote coordinators. */
function renderCreateAdminRemoteManagerSection(params: CreateAdminScopeParams): React.ReactNode {
  if (!params.isCoordinatorRemoteRole) return null;
  function handleChange(value: string): void {
    params.onFieldChange('managedById', value || undefined);
  }
  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <UserCog className="w-4 h-4" />
        Géré par
      </h4>
      <Autocomplete
        label="Géré par"
        required
        placeholder={createAdminRemoteManagerPlaceholder(params.currentUserRoleCode)}
        options={params.managersRemoteOptions}
        value={params.adminData.managedById || ''}
        onChange={handleChange}
        onSearch={params.onManagersRemoteSearch}
        isLoading={params.managersRemoteLoading}
        minSearchLength={2}
        disabled={roleMatches(params.currentUserRoleCode, 'manager', 'remote')}
        helperText={createAdminRemoteManagerHelperText(params.currentUserRoleCode)}
      />
    </div>
  );
}

/** Annual prospection-goal field for coordinators. */
function renderCreateAdminProspectionSection(params: CreateAdminScopeParams): React.ReactNode {
  if (!params.isCoordinatorRole || params.currentUserRoleCode !== 'admin') return null;
  function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
    params.onFieldChange('annualProspectionGoal', event.target.value);
  }
  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <Target className="w-4 h-4" />
        Objectif de prospection
      </h4>
      <Input
        label="Objectif annuel d'établissements à prospecter"
        type="number"
        min={0}
        value={params.adminData.annualProspectionGoal}
        onChange={handleChange}
        placeholder="Ex: 20"
        helperText="Nombre d'établissements à prospecter sur l'année (facultatif)"
      />
    </div>
  );
}

/** Establishments managed by a coordinator. */
function renderCreateAdminCoordinatorEstablishments(
  params: CreateAdminScopeParams,
): React.ReactNode {
  if (!params.hasInPersonMode || !params.isCoordinatorCategory) return null;
  if (params.selectedResidenceDepartmentCodes.length === 0) return null;
  function handleChange(values: string[]): void {
    params.onFieldChange('establishmentIds', values);
  }
  function handleBlur(): void {
    params.onFieldBlur('establishmentIds');
  }
  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <Building2 className="w-4 h-4" />
        Établissements gérés
      </h4>
      <MultiSelect
        label="Établissements"
        placeholder="Sélectionner les établissements"
        options={params.getEstabOptions(params.adminData.establishmentIds)}
        value={params.adminData.establishmentIds}
        onChange={handleChange}
        onBlur={handleBlur}
        onSearch={params.onEstabSearch}
        onLoadMore={params.onEstabLoadMore}
        hasMore={params.estabHasMore}
        isSearchLoading={params.estabSearchLoading || params.coordinatorEstabsLoading}
        searchPlaceholder="Rechercher un établissement..."
        minSearchLength={0}
        maxVisibleTags={2}
        showSelectAll={false}
        helperText="Établissements du/des département(s) sélectionné(s)"
        state={params.validation.getFieldState('establishmentIds')}
        error={params.validation.getFieldError('establishmentIds')}
        placement="top"
      />
    </div>
  );
}

/** Establishments managed by a manager. */
function renderCreateAdminManagerEstablishments(params: CreateAdminScopeParams): React.ReactNode {
  if (!params.hasInPersonMode || !params.isManagerCategory || params.isCoordinatorCategory) {
    return null;
  }
  function handleChange(values: string[]): void {
    params.onFieldChange('establishmentIds', values);
  }
  function handleBlur(): void {
    params.onFieldBlur('establishmentIds');
  }
  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <Building2 className="w-4 h-4" />
        Établissements gérés
      </h4>
      {renderCreateAdminManagerEstablishmentSelect(params, handleChange, handleBlur)}
    </div>
  );
}

/** Establishment {@link MultiSelect} for a manager. */
function renderCreateAdminManagerEstablishmentSelect(
  params: CreateAdminScopeParams,
  handleChange: (values: string[]) => void,
  handleBlur: () => void,
): React.ReactNode {
  if (params.establishmentSourceUserId) {
    return (
      <MultiSelect
        label="Établissements"
        placeholder="Sélectionner les établissements"
        options={mapScopedEstablishmentOptions(params.scopedEstablishments as never)}
        value={params.adminData.establishmentIds}
        onChange={handleChange}
        onBlur={handleBlur}
        maxVisibleTags={2}
        helperText="Établissements dont le manager aura la gestion (optionnel)"
        state={params.validation.getFieldState('establishmentIds')}
        error={params.validation.getFieldError('establishmentIds')}
        placement="top"
      />
    );
  }
  return (
    <MultiSelect
      label="Établissements"
      placeholder="Rechercher un établissement..."
      options={params.getEstabOptions(params.adminData.establishmentIds)}
      value={params.adminData.establishmentIds}
      onChange={handleChange}
      onBlur={handleBlur}
      onSearch={params.onEstabSearch}
      onLoadMore={params.onEstabLoadMore}
      hasMore={params.estabHasMore}
      isSearchLoading={params.estabSearchLoading}
      searchPlaceholder="Rechercher un établissement..."
      minSearchLength={2}
      maxVisibleTags={2}
      showSelectAll={false}
      helperText={ESTABLISHMENT_SEARCH_HINT}
      state={params.validation.getFieldState('establishmentIds')}
      error={params.validation.getFieldError('establishmentIds')}
      placement="top"
    />
  );
}

/** Department attachment picker. */
function renderCreateAdminDepartmentsSection(params: CreateAdminScopeParams): React.ReactNode {
  if (!params.showDepartmentField) return null;
  function handleChange(values: string[]): void {
    params.onFieldChange('departments', values);
  }
  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <MapPin className="w-4 h-4" />
        Département(s)
      </h4>
      <MultiSelect
        label="Département(s)"
        placeholder="Sélectionner les départements"
        options={mapDepartmentCodeOptions(params.departments)}
        value={params.adminData.departments}
        onChange={handleChange}
        maxVisibleTags={3}
        helperText="Département(s) de rattachement"
        required
      />
    </div>
  );
}

/** Programs managed by a coordinator. */
function renderCreateAdminProgramsSection(params: CreateAdminScopeParams): React.ReactNode {
  if (!params.showProgramField) return null;
  function handleChange(values: string[]): void {
    params.onFieldChange('programIds', values);
  }
  return (
    <div className="form-section">
      <h4 className="form-section-title">
        <BookOpen className="w-4 h-4" />
        Programme(s)
      </h4>
      <MultiSelect
        label="Programme(s)"
        placeholder="Sélectionner les programmes"
        options={mapProgramSelectOptions(params.programs)}
        value={params.adminData.programIds}
        onChange={handleChange}
        maxVisibleTags={3}
        helperText="Programme(s) gérés par ce coordinateur"
        required
        placement="top"
      />
    </div>
  );
}

/** All create-admin scope sections in order. */
function renderCreateAdminScopeSections(params: CreateAdminScopeParams): React.ReactNode {
  return (
    <>
      {renderCreateAdminManagerSection(params)}
      {renderCreateAdminResidenceSection(params)}
      {renderCreateAdminRemoteManagerSection(params)}
      {renderCreateAdminProspectionSection(params)}
      {renderCreateAdminCoordinatorEstablishments(params)}
      {renderCreateAdminManagerEstablishments(params)}
      {renderCreateAdminDepartmentsSection(params)}
      {renderCreateAdminProgramsSection(params)}
    </>
  );
}
